import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { isSafeModeConfigured } from "../onboarding/profile-completion.ts";
import { ENABLED_ONBOARDING_ROUTES, getStepRoute } from "../onboarding/steps.ts";
import { useAuthSession } from "./useAuthSession.ts";
import { usePhantomStatus } from "./usePhantomStatus.ts";
import { useUserProfile } from "./useUserProfile.ts";

interface AuthGateProps {
  children: ReactNode;
}

const AUTH_ROUTE = "/auth";
const LANDING_ROUTE = "/";
const AGE_ROUTE = getStepRoute("age");
const JURISDICTION_ROUTE = getStepRoute("jurisdiction");
const SAFE_MODE_ROUTE = getStepRoute("safe_mode");
const PLATFORM_ROUTE = getStepRoute("platform");
const PHANTOM_ROUTE = getStepRoute("phantom");

// Routes that render to unauthenticated visitors without redirecting them to
// /auth. Phase 1A Step 34 added the public landing surface at "/" — visiting
// the root URL must show the marketing landing, not bounce to sign-in.
// /auth itself is also public (sign-in form). Authenticated visitors hitting
// "/" get redirected to /home by AuthGate (see the session && onLandingRoute
// branch below).
//
// Phase JB.x fix — /daily-acca is the free shop window and MUST be
// anon-readable (sign-in is only required for slip-lock, trial-start,
// founder-claim, referral). /pricing + /pricing/success are anon entry
// points to Paystack checkout. /settings/display is a localStorage-only
// preferences page with no auth dependency.
const PUBLIC_ROUTES: readonly string[] = [
  LANDING_ROUTE,
  AUTH_ROUTE,
  "/daily-acca",
  "/pricing",
  "/pricing/success",
  "/settings/display",
  // Phase RV-5 — Upcoming Picks Board is anon-readable. Sign-in is only
  // required when the visitor clicks "Add to FLEX" / "Start a chain".
  "/rollover/upcoming",
  // Phase RV-6 — FLEX Builder. Anon-readable for the builder area; the
  // lock RPC requires auth and the panel surfaces a sign-in prompt inline.
  "/rollover/flex",
];

// Phase 1A onboarding gate.
//
// 1. Auth-loading → render children transparently (no splash).
// 2. Unauthenticated outside /auth → redirect /auth.
// 3. Authenticated on /auth → fall through to onboarding decision.
// 4. Profile-loading → render children transparently (no splash).
// 5. Compute the highest unmet onboarding gate (first match wins):
//      - confirmed_legal_age !== true → /onboarding/age
//      - country_code IS NULL or jurisdiction_supported !== true → /onboarding/jurisdiction
//      - safe_mode_config incomplete (see isSafeModeConfigured)    → /onboarding/safe-mode
//      - preferred_platform IS NULL                                → /onboarding/platform
//      - phantom_opt_out_at IS NULL AND no open phantom session    → /onboarding/phantom
// 6. If a gate is unmet, redirect there (or render the page if already on it).
// 7. If all gates pass, redirect away from any enabled onboarding route to
//    /home (post-Step-40 flip — was /brief in Step 28, /dashboard before that).
//    /brief, /vault, and /dashboard all remain fully reachable via direct
//    URL and via the discoverability links inside /home.
//
// Loop-safe because every redirect lands on a path that no longer triggers
// the same redirect.
export function AuthGate({ children }: AuthGateProps): JSX.Element {
  const { session, loading: authLoading } = useAuthSession();
  const { profile, loading: profileLoading } = useUserProfile();
  const { hasActiveSession: hasPhantomSession, loading: phantomLoading } = usePhantomStatus();
  const location = useLocation();
  const path = location.pathname;

  if (authLoading) {
    return <>{children}</>;
  }

  const onAuthRoute = path === AUTH_ROUTE;
  const onLandingRoute = path === LANDING_ROUTE;
  const onPublicRoute = PUBLIC_ROUTES.includes(path);

  if (!session && !onPublicRoute) {
    return <Navigate to={AUTH_ROUTE} replace />;
  }
  if (session && onAuthRoute) {
    return <Navigate to="/home" replace />;
  }
  if (session && onLandingRoute) {
    // Authenticated user typing the root URL goes straight to their app.
    return <Navigate to="/home" replace />;
  }
  if (!session) {
    // Unauthenticated visitor on a public route (/, /auth) — render through.
    return <>{children}</>;
  }

  // Authenticated. Wait for profile.
  if (profileLoading) {
    return <>{children}</>;
  }

  let neededGate: string | null = null;
  if (!profile || profile.confirmed_legal_age !== true) {
    neededGate = AGE_ROUTE;
  } else if (profile.country_code === null || profile.jurisdiction_supported !== true) {
    neededGate = JURISDICTION_ROUTE;
  } else if (!isSafeModeConfigured(profile)) {
    neededGate = SAFE_MODE_ROUTE;
  } else if (profile.preferred_platform === null) {
    neededGate = PLATFORM_ROUTE;
  } else {
    // Phantom Bet gate. Wait for phantom_sessions read before deciding so
    // a freshly-arriving user does not flicker through /onboarding/phantom
    // when an open session already exists.
    if (phantomLoading) {
      return <>{children}</>;
    }
    if (profile.phantom_opt_out_at === null && !hasPhantomSession) {
      neededGate = PHANTOM_ROUTE;
    }
  }

  if (neededGate !== null) {
    if (path !== neededGate) {
      return <Navigate to={neededGate} replace />;
    }
    return <>{children}</>;
  }

  // All gates passed.
  if (ENABLED_ONBOARDING_ROUTES.includes(path)) {
    return <Navigate to="/home" replace />;
  }
  return <>{children}</>;
}
